import React, { FC } from 'react';

import styled from '@emotion/styled';
import { LucidTheme, mqMin, variant, ComponentsType } from '@lucid/theme';
import dayjs from 'dayjs';

import Card from '../card/card';
import Heading from '../heading/heading';
import Link from '../link/link';
import Text from '../text/text';

export interface TileFields {
  /** The title for the `Tile`. */
  title: string;
  /** The date to display with the `Tile` component. */
  date?: string;
  /** The description paragraph for the `Tile` component. */
  description: string;
  /** The image src for the `Tile` component. */
  imageSrc: string;
  /** Checks if the tile should be sticky. */
  isSticky?: boolean;
  /** The link for the `Tile` component. */
  url: string;
  /** The unique id for the `Tile` component. */
  nid: string;
  /** The size for the individual `Tile` component. */
  size?: ComponentsType['TileSize'];
}

export interface TileProps<T extends TileFields> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The size of `Tile` components. */
  size?: ComponentsType['TileSize'];
  /** The `Tile` component data. */
  tile: T;
  /** The available variants for the `Tile` card component. */
  variant?: ComponentsType['Card'];
}

const StyledTile = styled(Card)(
  variant({
    scale: 'card',
    variants: {
      defaultVariant: {}
    }
  }),
  variant({
    prop: 'size',
    scale: 'tileSizes',
    variants: {
      defaultVariant: {}
    }
  })
);

const Tile: FC<TileProps<TileFields>> = (
  {
    className,
    variant = 'interactive',
    size = 'medium',
    tile,
    ...rest
  }
) => {
  const isMedium = size === 'medium';
  const isLarge = size === 'large';

  return (
    <StyledTile
      {...rest}
      className={className}
      css={(theme: LucidTheme) => [
        {
          label: 'tileCard',
          background: theme.colors.white,
          height: '100%',
          maxHeight: '580px',
          width: '100%',
          maxWidth: '580px',
          marginBottom: theme.space[5],
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.25s cubic-bezier(0.17, 0.67, 0.52, 0.97)',
          border: `1px solid ${theme.colors.coolGrey[10]}`,
          [mqMin.tabletSmall]: {
            maxHeight: '420px'
          },
          '&:hover': {
            transform: 'translateY(-5px)',
            ['& .tileContent']: {
              '&::after': {
                transform: 'scaleX(1)',
                transformOrigin: 'bottom left'
              }
            },
            ['& .tileTitle']: {
              color: theme.colors.primary
            }
          },
          '&:hover, &:focus': {
            borderColor: theme.colors.primary
          }
        },
        isMedium && {
          [mqMin.tabletSmall]: {
            maxHeight: '270px',
            marginBottom: theme.space[6]
          }
        },
        isLarge && {
          [mqMin.tabletSmall]: {
            maxHeight: '580px'
          }
        }
      ]}
      variant={variant}
    >
      <Link
        newTab
        css={[
          {
            height: '100%',
            overflow: 'hidden',
            width: '100%'
          },
          isMedium && {
            [mqMin.tabletSmall]: {
              display: 'flex'
            }
          }
        ]}
        to={tile.url}
        underline="none"
      >
        <Card.Image
          isBackgroundImage
          alt={tile.title || 'Tile Background Image'}
          className="tileImageContainer"
          css={[
            {
              label: 'tileImageContainer',
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '285px',
              '&::before': {
                display: 'block',
                content: '""',
                paddingTop: '56.25%'
              },
              '& > .backgroundImage': {
                label: 'tileBGImage',
                height: '100%',
                width: '100%',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundClip: 'content-box',
                position: 'absolute',
                top: 0,
                bottom: 0
              }
            },
            isMedium && {
              [mqMin.tabletSmall]: {
                '&::before': {
                  paddingTop: '100%'
                }
              }
            },
            isLarge && {
              [mqMin.tabletSmall]: {
                height: '256px'
              },
              [mqMin.computer]: {
                maxHeight: '290px',
                height: 'unset'
              }
            }
          ]}
          src={`'${tile.imageSrc}'` || 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/examples/org-chart/org-chart-template.jpg'}
        />
        <Card.Content
          className="tileContent"
          css={(theme: LucidTheme) => [
            {
              background: theme.colors.white,
              color: theme.colors.coolGrey[100],
              padding: theme.space[5],
              position: 'relative',
              height: '100%',
              maxHeight: '375px',
              verticalAlign: 'top',
              [mqMin.mobile]: {
                maxHeight: '285px'
              },
              [mqMin.tabletSmall]: {
                maxHeight: '322px'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                boxShadow: `0 -30px 40px 5px ${theme.colors.white} inset`,
                borderTop: `6px solid ${theme.colors.coolGrey[20]}`
              },
              '&::after': {
                content: '""',
                width: '100%',
                height: '6px',
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: theme.colors.primary,
                transformOrigin: 'bottom right',
                transform: 'scaleX(0)',
                transition: 'transform 0.25s ease-out',
                zIndex: theme.zIndices[1]
              }
            },
            isLarge && {
              [mqMin.tabletSmall]: {
                width: '100%',
                height: '50%',
                display: 'block'
              },
              [mqMin.computer]: {
                maxHeight: '290px',
                paddingTop: '38px'
              }
            },
            isMedium && {
              [mqMin.tabletSmall]: {
                maxHeight: '270px',
                maxWidth: '50%'
              }
            }
          ]}
        >
          {tile?.date &&
            (
              <Text
                css={(theme: LucidTheme) => [
                  {
                    marginTop: theme.space[2],
                    marginBottom: theme.space[3]
                  },
                  isMedium && {
                    [mqMin.tabletSmall]: {
                      marginTop: theme.space[0]
                    }
                  }
                ]}
                variant="paragraphS300"
              >
                {dayjs(tile.date)
                  .subtract(6, 'hour')
                  .format('MMMM DD, YYYY')}
              </Text>
            )}
          <Heading
            as="h3"
            className="tileTitle"
            css={(theme: LucidTheme) => [
              {
                color: theme.colors.coolGrey[100],
                marginTop: theme.space[2],
                marginBottom: theme.space[2]
              },
            ]}
            variant="headlineXL"
          >
            {tile.title}
          </Heading>
          <Text
            css={[
              isLarge && {
                [mqMin.tabletLandscape]: {
                  minHeight: '514px'
                }
              }
            ]}
            variant="paragraphS400"
          >
            {tile.description}
          </Text>
        </Card.Content>
      </Link>
    </StyledTile>
  );
};

export { Tile };
export default Tile;
